<template>
  <v-dialog v-model="dialog" max-width="900px" persistent>
    <v-card>
      <v-card-title class="headline">
        {{ $t("modify_room_types") }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <!-- Left Column: Proposed Types -->
          <v-col cols="6">
            <h3 class="subtitle-1 font-weight-bold mb-2">
              {{ $t("proposed_room_types") }}
            </h3>

            <v-card outlined class="pa-2 column-card">
              <v-list dense class="transparent py-0">
                <v-list-item
                  v-for="(type, index) in availableProposedTypes"
                  :key="`proposed-${index}`"
                  class="type-item mb-1 elevation-1 rounded-lg white px-2"
                  @click="addType(type)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ type.name }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="ma-0">
                    <v-btn icon small>
                      <v-icon color="primary">mdi-plus</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div
                v-if="availableProposedTypes.length === 0"
                class="text-center pa-4 grey--text"
              >
                {{ $t("no_proposed_types") }}
              </div>
            </v-card>
          </v-col>

          <!-- Right Column: Selected Types -->
          <v-col cols="6">
            <h3 class="subtitle-1 font-weight-bold mb-2">
              {{ $t("selected_room_types") }}
            </h3>

            <v-card outlined class="pa-2 column-card">
              <!-- Add Custom Type -->
              <div class="d-flex align-center mb-2">
                <v-text-field
                  v-model="newCustomType"
                  :label="$t('add_custom_type')"
                  dense
                  outlined
                  hide-details
                  rounded
                  class="white custom-type-input"
                  @keyup.enter="addCustomType"
                ></v-text-field>
                <v-btn
                  color="secondary"
                  small
                  icon
                  @click="addCustomType"
                  :disabled="!newCustomType"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </div>

              <div class="text-caption grey--text mb-2">
                {{ $t("drag_to_reorder") }}
              </div>

              <v-list dense id="selected-types-list" class="transparent py-0">
                <v-list-item
                  v-for="(type, index) in visibleMappings"
                  :key="`selected-${type.id || type.name}`"
                  class="type-item mb-1 elevation-1 rounded-lg white px-0"
                >
                  <v-list-item-action
                    class="ma-0 drag-handle"
                    style="cursor: grab"
                  >
                    <v-icon>mdi-drag-vertical</v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ type.name }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="ma-0">
                    <v-btn icon small @click="removeSelectedType(index)">
                      <v-icon color="grey">
                        {{ type.is_default ? "mdi-eye-off" : "mdi-delete" }}
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div
                v-if="visibleMappings.length === 0"
                class="text-center pa-4 grey--text"
              >
                {{ $t("no_types_selected") }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">{{ confirmTitle }}</v-card-title>
        <v-card-text>{{ confirmMessage }}</v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="executeConfirm"
          >
            {{ $t("accept") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="confirmDialog = false"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import RoomTypeService from "@/services/RoomTypeService";
import Sortable from "sortablejs";

export default {
  name: "aRoomTypeConfigModal",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    initialSelectedTypes: {
      type: Array,
      default: () => []
    },
    hotelId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      dialog: this.value,
      newCustomType: "",
      selectedTypes: [],
      sortableInstance: null,
      // Confirmation state
      confirmDialog: false,
      confirmTitle: "",
      confirmMessage: "",
      pendingAction: null
    };
  },
  computed: {
    availableProposedTypes() {
      return this.selectedTypes.filter(m => m.is_default && !m.visible);
    },
    visibleMappings() {
      return this.selectedTypes.filter(m => m.visible);
    },
    hiddenMappings() {
      return this.selectedTypes.filter(m => !m.visible);
    }
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
        this.initializeData();
        this.$nextTick(() => {
          this.initSortable();
        });
      }
    },
    dialog(val) {
      this.$emit("input", val);
    }
  },
  methods: {
    initializeData() {
      // Deep copy to avoid mutating prop directly before saving
      if (this.initialSelectedTypes && this.initialSelectedTypes.length > 0) {
        this.selectedTypes = JSON.parse(
          JSON.stringify(this.initialSelectedTypes)
        );
      } else {
        this.selectedTypes = [];
      }
    },
    isSelected(type) {
      return this.selectedTypes.some(t => t.name === type.name);
    },
    initSortable() {
      const el = document.getElementById("selected-types-list");
      if (el) {
        if (this.sortableInstance) this.sortableInstance.destroy();

        this.sortableInstance = Sortable.create(el, {
          animation: 150,
          handle: ".drag-handle", // Drag handle
          onEnd: this.onReorder
        });
      }
    },
    askConfirmation(title, message, action) {
      this.confirmTitle = title;
      this.confirmMessage = message;
      this.pendingAction = action;
      this.confirmDialog = true;
    },
    executeConfirm() {
      if (this.pendingAction) {
        this.pendingAction();
      }
      this.confirmDialog = false;
      this.pendingAction = null;
    },
    addType(type) {
      this.askConfirmation(
        this.$t("add_room_type"),
        this.$t("confirm_add_room_type", { name: type.name }),
        () => {
          // Check if it already exists but is hidden
          const existing = this.selectedTypes.find(t => t.name === type.name);

          if (existing) {
            // If it exists and is hidden, toggle visibility to true
            if (!existing.visible) {
              this.toggleVisibility(existing);
            }
            return;
          }

          // Create new mapping via API
          const newMapping = {
            hotel_id: this.hotelId,
            name: type.name,
            visible: true
            // position will be handled by backend or we append to end
          };

          this.$store.dispatch("setSnackbar", {
            title: this.$t("adding_room_type"),
            color: "secondary"
          });

          RoomTypeService.setRoomTypeMapping(newMapping)
            .then(response => {
              if (response && response.data) {
                this.selectedTypes.push(response.data);
                this.$emit("mapping-changed"); // Notify parent to refresh or update
                this.$store.dispatch("setSnackbar", {
                  title: this.$t("room_type_added"),
                  color: "accent"
                });
              }
            })
            .catch(err => {
              //console.error("Error creating mapping", err);
              this.$store.dispatch("setSnackbar", {
                title: this.$t("room_type_not_added"),
                color: "error"
              });
            });
        }
      );
    },
    toggleVisibility(mapping) {
      const updatedMapping = {
        hotel_id: this.hotelId,
        mapping_id: mapping.id,
        name: mapping.name,
        visible: !mapping.visible
      };

      const actionKey = mapping.visible
        ? "hiding_room_type"
        : "showing_room_type";
      const successKey = mapping.visible
        ? "room_type_hidden"
        : "room_type_shown";

      this.$store.dispatch("setSnackbar", {
        title: this.$t(actionKey),
        color: "secondary"
      });

      RoomTypeService.setRoomTypeMapping(updatedMapping)
        .then(() => {
          // Update local state
          const index = this.selectedTypes.findIndex(t => t.id === mapping.id);
          if (index !== -1) {
            // We need to update the original object in selectedTypes
            // We can't just replace it with updatedMapping because updatedMapping has different keys (mapping_id vs id)
            // So we update the properties of the existing object
            const newLocalState = {
              ...this.selectedTypes[index],
              visible: !mapping.visible
            };
            this.$set(this.selectedTypes, index, newLocalState);
          }
          this.$emit("mapping-changed");
          this.$store.dispatch("setSnackbar", {
            title: this.$t(successKey),
            color: "accent"
          });
        })
        .catch(err => {
          //console.error("Error toggling visibility", err);
          this.$store.dispatch("setSnackbar", {
            title: this.$t("something_went_wrong"),
            color: "error"
          });
        });
    },
    deleteMapping(mapping) {
      if (mapping.is_default) return; // Should not happen if UI is correct

      this.$store.dispatch("setSnackbar", {
        title: this.$t("deleting_room_type"),
        color: "secondary"
      });

      RoomTypeService.deleteRoomTypeMapping({ mapping_id: mapping.id })
        .then(() => {
          const index = this.selectedTypes.findIndex(t => t.id === mapping.id);
          if (index !== -1) {
            this.selectedTypes.splice(index, 1);
          }
          this.$emit("mapping-changed");
          this.$store.dispatch("setSnackbar", {
            title: this.$t("room_type_deleted"),
            color: "accent"
          });
        })
        .catch(err => {
          //console.error("Error deleting mapping", err);
          this.$store.dispatch("setSnackbar", {
            title: this.$t("room_type_not_deleted"),
            color: "error"
          });
        });
    },
    onReorder(evt) {
      const visible = this.visibleMappings;
      const movedItem = visible.splice(evt.oldIndex, 1)[0];
      visible.splice(evt.newIndex, 0, movedItem);

      // Now we have the new order of visible items.
      const newOrderIds = visible.map(m => m.id);

      this.$store.dispatch("setSnackbar", {
        title: this.$t("setting_room_type_position"),
        color: "secondary"
      });

      RoomTypeService.reorderRoomTypeMappings({
        hotel_id: this.hotelId,
        mapping_ids: newOrderIds
      })
        .then(() => {
          this.$emit("mapping-changed");
          this.$store.dispatch("setSnackbar", {
            title: this.$t("room_type_position_changed"),
            color: "accent"
          });
        })
        .catch(err => {
          //console.error("Error reordering mappings", err);
          this.$store.dispatch("setSnackbar", {
            title: this.$t("room_type_position_not_changed"),
            color: "error"
          });
        });
    },
    addCustomType() {
      if (!this.newCustomType.trim()) return;

      const newTypeName = this.newCustomType.trim();

      // Check if already exists in selectedTypes (which now contains ALL mappings)
      const existing = this.selectedTypes.find(
        t => t.name.toLowerCase() === newTypeName.toLowerCase()
      );

      if (existing) {
        // If it exists but is hidden, make it visible
        if (!existing.visible) {
          this.toggleVisibility(existing);
        }
        // If already visible, do nothing
        this.newCustomType = "";
        return;
      }

      // Create brand new custom mapping
      const newMapping = {
        hotel_id: this.hotelId,
        name: newTypeName,
        visible: true
      };

      this.$store.dispatch("setSnackbar", {
        title: this.$t("adding_room_type"),
        color: "secondary"
      });

      RoomTypeService.setRoomTypeMapping(newMapping)
        .then(response => {
          if (response && response.data) {
            this.selectedTypes.push(response.data);
            this.$emit("mapping-changed");
            this.$store.dispatch("setSnackbar", {
              title: this.$t("room_type_added"),
              color: "accent"
            });
          }
        })
        .catch(err => {
          //console.error("Error creating custom mapping", err);
          this.$store.dispatch("setSnackbar", {
            title: this.$t("room_type_not_added"),
            color: "error"
          });
        });

      this.newCustomType = "";
    },
    removeCustomType(index) {
      // this.customTypes.splice(index, 1);
    },
    removeSelectedType(index) {
      // This is triggered by the delete button in the list
      // index is index in visibleMappings
      const mapping = this.visibleMappings[index];
      if (!mapping) return;

      if (mapping.is_default) {
        // Default mappings cannot be deleted, only hidden
        this.askConfirmation(
          this.$t("hiding_room_type"),
          this.$t("confirm_hide_room_type", { name: mapping.name }),
          () => {
            this.toggleVisibility(mapping);
          }
        );
      } else {
        // Custom mappings can be deleted
        this.askConfirmation(
          this.$t("deleting_room_type"),
          this.$t("confirm_delete_custom_room_type", { name: mapping.name }),
          () => {
            this.deleteMapping(mapping);
          }
        );
      }
    },
    close() {
      this.dialog = false;
    },
    save() {
      this.$emit("save", this.selectedTypes);
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
.column-card {
  height: calc(100% - 25px);
  border: none;
  background-color: #f4f4f4;
}
.custom-type-input {
  ::v-deep .v-input__slot {
    min-height: auto !important;
    .v-label {
      top: 6px;
      font-size: 13px;
    }
    input {
      font-size: 13px;
      padding: 10px 0;
      line-height: 1;
    }
  }
}
.type-item {
  cursor: pointer;
}
.type-item:hover {
  background-color: #f5f5f5 !important;
}
</style>
